/**
 * The corporation feature (M6 plan P33): in the Reach with a career,
 *
 * - registers the Registration tab (`src/ui/corp/registration.tsx`) into the station services registry once: the
 *   founding form at the Charter Hub, then the Corporation screen there. It reaches the Corporation screen through
 *   that tab rather than a new binding or menu tab (P31 owns the bindings and the menu this wave).
 * - wires the corporation look (D-393, D-397): P22's `bindCorpLook` dresses every own ship promoted to a real ship in
 *   the `player_corp` look (the ship views' `setLook`), and any other promotion back in its def's look (entity ids
 *   are recycled); `setCorpLivery` paints that look from `CorpRecord.livery` on load and on founding or a repaint.
 *
 * Init-time only; nothing per frame.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import corpJson from '../../data/strategic/corp.json'
import { bindCorpLook } from '../../fleet-link/presence'
import type { FleetService } from '../../fleet-link/service'
import { setCorpLivery } from '../../render/materials'
import type { StationServiceRegistry } from '../../reach/ports'
import type { LiveryColors } from '../../strategic/types'
import { createRegistrationPanel } from '../../ui/corp/registration'
import charterBoard from '../charterBoard/feature'

/** After the fleet link (strategic + 2) and the fleet panels (strategic + 3). */
const ORDER = FEATURE_ORDER.strategic + 4

const registered = new WeakSet<StationServiceRegistry>()

/** A per-game logo kit seed: the Career's seed (uint32), mixed so the drafts differ from the space RNG's stream. */
function seedOf(career: Career | null): number {
  return career === null ? 0x5eed : Math.imul(career.seed ^ 0x6c6f676f, 2654435761) >>> 0
}

const feature: GameFeature & { dispose(): void } = {
  name: 'corpPanels',
  order: ORDER,
  init(ctx: GameContext): void {
    const reach = ctx.reach
    if (reach === null) return
    const client = ctx.services.maybe('strategic')
    const m2 = ctx.services.maybe('m2')
    if (client === undefined || m2 === undefined) return
    const lib = ctx.lib
    const views = ctx.views
    const applyLivery = (livery: LiveryColors): void => setCorpLivery(lib, livery)

    // The corporation look on own real ships; the colours from the corporation record.
    bindCorpLook(
      (eid) => views.setLook(eid, 'player_corp'),
      (eid) => views.setLook(eid, null),
    )
    client
      .corpView()
      .then((v) => applyLivery(v.livery))
      .catch((err: unknown) => logger.warn('corp: could not read the corporation livery', err))

    if (registered.has(reach.services)) return
    registered.add(reach.services)
    const career = (): Career | null => {
      try {
        return m2.career()
      } catch {
        return null
      }
    }
    const systemNames = new Map(reach.galaxy.systems.map((s) => [s.id, s.name]))
    reach.services.register(
      createRegistrationPanel({
        fleet: (): FleetService | null => ctx.services.maybe('fleet') ?? null,
        client,
        career,
        registrationTemplate: corpJson.founding.registrationTemplate,
        logoSeed: seedOf(career()),
        applyLivery,
        openBoard: () => charterBoard.openBoard(),
        systemName: (id) => systemNames.get(id) ?? id,
        story: () => ctx.services.maybe('story') ?? null,
      }),
    )
  },
  dispose(): void {
    bindCorpLook(null)
  },
}

export default feature
